import { apiFiles } from "@/api/file.api.ts";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { getEsignAuthMethodLabel } from "@/pages/esign/esign.config.ts";
import { useEsignEvidenceReport } from "@/queries/use-esign.query.ts";
import { EsignEvidenceEventType, EsignSignatureType } from "@/types/esign.types.ts";
import {
  Avatar,
  Box,
  Button,
  ButtonIcon,
  DotSeparator,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface EsignSignatureCertificateModalProps extends ModalProps {
  envelopeId: string;
  requestPublicId: string;
}

export function EsignSignatureCertificateModal({
  envelopeId,
  requestPublicId,
  ...props
}: EsignSignatureCertificateModalProps) {
  const { t } = useTranslation();
  const { renderDate, renderTime, renderDateAndTime, renderEmployeeName } = useFormatter();
  const { downloadFile, isExporting } = useDownloadHelper();
  const { data: report, isLoading } = useEsignEvidenceReport(envelopeId);

  const signer = report?.signers.find((item) => item.request_public_id === requestPublicId);
  const envelope = report?.envelope;

  const isAcknowledge = envelope?.signature_type === EsignSignatureType.acknowledge;
  const signedFileUuid = envelope?.signed_file_uuid ?? undefined;
  const handleDownload = () => {
    if (!signedFileUuid) return;
    downloadFile({ apiCall: () => apiFiles.getPrivate(signedFileUuid) });
  };

  const sentEntry = report?.timeline.find(
    (entry) => entry.event_type === EsignEvidenceEventType.envelope_created
  );
  const timeline = [
    sentEntry && { label: t("common:sent"), date: sentEntry.datetime_created_at },
    signer?.datetime_signed_at && {
      label: t(isAcknowledge ? "core:esign_status_acknowledged" : "core:esign_status_signed"),
      date: signer.datetime_signed_at,
    },
  ].filter((entry): entry is { label: string; date: Date } => Boolean(entry));

  const authLabel = signer ? getEsignAuthMethodLabel(signer.auth_method, t) : "";

  return (
    <Modal
      {...props}
      title={t(
        isAcknowledge ? "core:esign_acknowledgment_certificate" : "core:esign_signature_certificate"
      )}
      variant="middle"
      layout="stretch"
      size="md"
      withCloser
      actions={
        signedFileUuid ? (
          <Button className="w-full" size="lg" onClick={handleDownload} disabled={isExporting}>
            {t(
              isAcknowledge
                ? "core:esign_download_acknowledged_document"
                : "core:esign_download_signed_document"
            )}
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <Stack gap="lg">
          <Stack direction="horizontal" items="center" gap="md">
            <Skeleton className="h-20 w-20 rounded-full" />
            <Stack gap="sm" className="flex-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-32" />
            </Stack>
          </Stack>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </Stack>
      ) : signer && envelope ? (
        <Stack gap="lg">
          <Stack gap="md">
            <EmployeeAvatar employee={signer.signer} size="2xl" />
            <Stack gap="xs">
              <Title
                header={t(isAcknowledge ? "core:esign_acknowledged_by" : "core:esign_signed_by")}
                size="xs"
                className="mt-4"
              />
              <Text className="text-lg text-title">{renderEmployeeName(signer.signer)}</Text>
              {signer.datetime_signed_at && (
                <DotSeparator>
                  <Text className="text-secondary">
                    {`${t("core:esign_signed_on_prefix")} ${renderDate(signer.datetime_signed_at)}`}
                  </Text>
                  <Text className="text-secondary">{renderTime(signer.datetime_signed_at)}</Text>
                </DotSeparator>
              )}
            </Stack>
          </Stack>

          <Stack>
            <Title header={t("core:document")} size="xs" />
            <Box className="rounded-xs bg-dark/5 py-4 px-6">
              <ListItem
                icon={
                  <Avatar
                    className="bg-positive"
                    icon={
                      <FontAwesomeIcon
                        icon={
                          isAcknowledge ? "fa-light fa-file-circle-check" : "fa-light fa-signature"
                        }
                        className="text-light"
                      />
                    }
                    size="md"
                  />
                }
                title={envelope.document_title}
                behavior="card"
                caption={envelope.signed_document_title ?? undefined}
                valueSlot={
                  signedFileUuid ? (
                    <ButtonIcon
                      variant="secondary"
                      icon={<FontAwesomeIcon icon="fa-light fa-cloud-arrow-down" />}
                    />
                  ) : undefined
                }
                onClick={signedFileUuid ? handleDownload : undefined}
              />
            </Box>
          </Stack>

          {timeline.length > 0 && (
            <Stack gap="xs">
              <Title header={t("core:esign_evidence_timeline")} size="xs" />
              <Box className="rounded-xs bg-dark/5 py-4 px-6">
                {timeline.map((entry) => (
                  <ListItem
                    key={entry.label}
                    icon={<Avatar size="md" icon={<FontAwesomeIcon icon="fa-light fa-check" />} />}
                    preTitle={entry.label}
                    title={renderDateAndTime(entry.date)}
                  />
                ))}
              </Box>
            </Stack>
          )}

          <Stack gap="xs">
            <Title header={t("core:esign_evidence_verification")} size="xs" />
            <Box className="rounded-xs bg-dark/5 py-4 px-6">
              {signer.ip && <ListItem preTitle={t("core:esign_evidence_ip")} title={signer.ip} />}
              {authLabel && <ListItem preTitle={t("core:authentication")} title={authLabel} />}
            </Box>
          </Stack>
        </Stack>
      ) : undefined}
    </Modal>
  );
}
